import React from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { get, toSafeInteger } from 'lodash';
import moment from 'moment-timezone'
import {
  VenuePage,
  Icons,
  Panel,
  SecurityHelper,
  ScrollToHelper,
  MediaGallery,
  Button,
  Heading,
  DisplayHtmlContent,
  Placeholder,
  TYPES
} from 'sarsaparilla';
import { getCurrentTime, hasLotteryApplication, readRuleAsMoment } from 'ui-permit/src/utils/utils';
import PermitMap from 'ui-permit/src/shared/components/PermitMap';
import PermitVenueDates from 'ui-permit/src/components/PermitVenueDates';
import RightRailContainer from 'ui-permit/shared-components/venuePage/RightRail';
import InventoryReviewsSection from 'ui-rating-review/src/containers/InventoryReviewsSection';
import {
  RatingSummaryContainer,
  CellSummaryContainer,
} from 'ui-rating-review/src/containers/AggregateBar';
import PermitAvailability from './PermitAvailability';
import * as pActions from '../actions/permit';
import * as aActions from '../actions/availability';
import { MT_WHITNEY_PERMIT_ID } from '../utils/inyoUtils';
import { getFirstDivision } from '../utils/permitUtils';

const propTypes = {
  match: TYPES.MATCH,
  permit: PropTypes.shape({
    activities: PropTypes.array,
    addresses: PropTypes.array,
    description: PropTypes.shape({
      cancellation_policy: PropTypes.string,
      description: PropTypes.string,
      discounts: PropTypes.string,
      driving_directions: PropTypes.string,
      fee_policy: PropTypes.string,
      need_to_know_permit: PropTypes.string,
      no_show_policy: PropTypes.string,
      quota_details: PropTypes.string,
      season_information: PropTypes.string
    }),
    divisions: PropTypes.object,
    id: PropTypes.string,
    indexed_rules: PropTypes.shape({
      SeasonStart: PropTypes.shape({
        value: PropTypes.number
      }),
      SeasonEnd: PropTypes.shape({
        value: PropTypes.number
      }),
      HighUseRegistrationStart: PropTypes.shape({
        value: PropTypes.number
      }),
      HighUseRegistrationEnd: PropTypes.shape({
        value: PropTypes.number
      }),
      LotteryResultsAvailable: PropTypes.shape({
        value: PropTypes.number
      }),
      CancellationDeadline: PropTypes.shape({
        value: PropTypes.number
      }),
      HighUseSeasonStart: PropTypes.shape({
        value: PropTypes.number
      }),
      HighUseSeasonEnd: PropTypes.shape({
        value: PropTypes.number
      }),
      QuotaPerDay: PropTypes.shape({
        value: PropTypes.number
      })
    }),
    latitude: PropTypes.number,
    links: PropTypes.array,
    longitude: PropTypes.number,
    lotteries: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        is_active: PropTypes.boolean
      })
    ),
    name: PropTypes.string,
    needToKnow: PropTypes.arrayOf(PropTypes.string),
    phone: PropTypes.string,
    recareas: PropTypes.array,
    lottery_fee: PropTypes.string,
    recreation_fee: PropTypes.string,
    reservation_fee: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.shape({
      activeTab: PropTypes.string,
      date: PropTypes.string
    })
  }),
  recareas: PropTypes.array,
  dispatch: PropTypes.func,
  errorList: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      error: PropTypes.string
    })
  ])),
  recarea: PropTypes.arrayOf(PropTypes.object),
  recareaError: PropTypes.string,
  fetchErr: PropTypes.string,
  userIssuances: PropTypes.arrayOf(PropTypes.shape({})),
  aggSeasonRules: PropTypes.array,
};

const defaultProps = {
  permit: {
    links: [],
    name: ''
  },
  recarea: []
};

export class PermitVenuePage extends React.Component {
  static displayName = 'PermitVenuePage';

  constructor(props) {
    super(props);

    this.state = {
      disableCalendar: true,
      readMore: false,
      isLoadingPermitContent: true,
      activeTabIndex: toSafeInteger(get(this.props, "location.query.activeTab"))
    };

    this.permitId = props.match.params.permitId;

    this.handleImportantDatesClick = this.handleImportantDatesClick.bind(this);
    this.handleDivisionSelection = this.handleDivisionSelection.bind(this);
    this.showTheLoginModal = this.showTheLoginModal.bind(this);
    this.toggleReadMore = this.toggleReadMore.bind(this);
  }

  async componentDidMount() {
    const permitId = this.permitId;
    const promises = [this.getMapfeatures()];
    const permit = await this.props.dispatch(pActions.fetchPermit(permitId))
    const recAreaId = get(permit, 'recareas[0]');
    const recAreaName = get(this.props, 'recarea.[0].name');

    this.props.dispatch(pActions.fetchAggregateSeason(permitId));
    this.props.dispatch(pActions.fetchLotteries(permitId));

    if (recAreaId && !recAreaName) {
      promises.push(this.props.dispatch(pActions.fetchRecarea(recAreaId)));
    }
    await Promise.all(promises);
    this.setState({ isLoadingPermitContent: false });
  }

  getMapfeatures() {
    return fetch(`/shared/inyonwhitney/data/${this.permitId}/features.json`, {
      credentials: 'include',
    })
      .then(response => response.json())
      .then(json => {
        this.setState({
          lines: json.lines,
          points: json.points,
          polygons: json.polygons,
        });
      })
      .catch(() => {});
  }

  toggleReadMore() {
    this.setState((prevState) => ({
      readMore: !prevState.readMore
    }));
  }

  execFetchAvailability(permitId, divisionId) {
    let _divisionId = divisionId;

    if (divisionId === '' || divisionId === undefined) {
      // if permit has multiple divisions, don't get any
      if (this.props.permit.divisions && Object.keys(this.props.permit.divisions).length > 1) {
        return;
      }

      // get the division if there's only 1 division for the permit
      const aDivision = getFirstDivision(this.props.permit.divisions);

      this.setState({
        division: aDivision
      });
      _divisionId = aDivision.id;
    }

    this.props.dispatch(aActions.fetchAvailabilityOneDivision(
      permitId,
      _divisionId,
      new Date(this.props.permit.indexed_rules.SeasonStart.value),
      new Date(this.props.permit.indexed_rules.SeasonEnd.value),
      SecurityHelper.isCommercialAccount()),
    );
  }

  // handles the event of changing the selected division in the availability widget
  handleDivisionSelection(divisionId) {
    this.setState({ division: this.props.permit.divisions[divisionId] });

    if (divisionId === "") {
      this.setState({ disableCalendar: true });
    }
    else {
      this.setState({ disableCalendar: false });
    }

    this.execFetchAvailability(this.props.permit.id, divisionId);
  }

  showTheLoginModal() {
    this.props.dispatch(pActions.showLoginModal());
  }

  getBreadcrumbsPath() {
    const { permit, recarea } = this.props;
    const recAreaName = recarea?.[0]?.name;
    const recAreaId = recarea?.[0]?.entity_id;
    const gatewaysURL = `/camping/gateways/${recAreaId}`;
    const path = [{ title: 'Home', url: '/' }];
    if (recAreaName && recAreaId) {
      path.push({ title: recAreaName, url: gatewaysURL });
    }
    path.push({ title: permit.name });
    return path;
  }

  handleImportantDatesClick() {
    this.setState({ activeTabIndex: 0 }, () => {
      const el = document.getElementById('important-dates');
      const header = document.querySelector('header');
      const y = el.offsetTop - header.offsetHeight;
      ScrollToHelper.scrollToY(y);
    });
  }

  handleOnActiveTabIndexChange = (newActiveTabIndex) => {
    this.setState({ activeTabIndex: newActiveTabIndex })
  }

  tabsToShow() {
    const { fetchErr } = this.props;
    if (this.state.isLoadingPermitContent || fetchErr) return [];
    return [
      {
        label: 'Overview',
        content: this.renderOverview(),
      },
      {
        label: 'Need to Know',
        content: this.renderNeedToKnow(),
      },
      {
        label: 'Fees & Cancellations',
        content: this.renderPolicy(),
      },
      {
        label: 'Getting Here',
        content: this.renderGettingHere(),
      },
      {
        label: 'Contacts',
        content: this.renderContacts(),
      },
    ];
  }

  renderOverview() {
    const {
      description,
      season_information: seasonInformation,
      quota_details: quotaDetails
    } = this.props.permit.description;
    const { readMore } = this.state;

    return (
      <Panel label="Overview">
        <div className="content-section">
          <div className={`overview-inner-wrapper ${this.state.readMore ? 'expanded' : 'collapsed'}`}>
            <DisplayHtmlContent html={description} />
          </div>
          <div className="permit-collapse-button">
            <Button
              appearance="link"
              iconBeforeElement={readMore ? <Icons.IconChevronUp /> : <Icons.IconChevronDown />}
              onClick={this.toggleReadMore}
              screenReaderTextBefore={`${readMore ? 'Hide' : 'Show'} additional overview text`}>
              {`Read ${readMore ? 'Less' : 'More'}`}
            </Button>
          </div>
        </div>

        {seasonInformation &&
          <div className="content-section">
            <Heading appearance="h5" headingLevel={2}>
              Permit & Season Information
            </Heading>
            <DisplayHtmlContent html={seasonInformation} />
          </div>
        }

        <div className="content-section">
          <PermitVenueDates permit={this.props.permit} />
        </div>

        {quotaDetails && (
          <div className="content-section">
            <Heading appearance="h5" headingLevel={2}>
              How Does the Quota Work for this Permit?
            </Heading>
            <DisplayHtmlContent html={quotaDetails} />
          </div>
        )}

        <MediaGallery
          assetId={this.permitId}
          className="content-section" />

        <InventoryReviewsSection
          locationType="Permit"
          locationId={this.props.permit.id}
          className="content-section" />
      </Panel>
    );
  }

  renderNeedToKnow() {
    const {
      need_to_know_permit: needToKnowPermit
    } = this.props.permit.description;

    return (
      <Panel label="Need to Know">
        {this.renderContent(needToKnowPermit)}
      </Panel>
    );
  }

  renderContent(body, title) {
    if (body) {
      return (
        <React.Fragment>
          {title && (
            <Heading appearance="h5" headingLevel={2}>
              {title}
            </Heading>)}
          <DisplayHtmlContent html={body} />
        </React.Fragment>
      );
    }
    return null;
  }

  renderPolicy() {
    const {
      fee_policy: feePolicy,
      cancellation_policy: cancellationPolicy,
      no_show_policy: noShowPolicy,
      discounts
    } = this.props.permit.description;

    return (
      <Panel label="Fees & Cancellations">
        <div className="content-section">
          {this.renderContent(feePolicy, "Fee Policies")}
        </div>
        <div className="content-section">
          {this.renderContent(cancellationPolicy, "Cancellation & Change Policies")}
        </div>
        {noShowPolicy && (<div className="content-section">
          {this.renderContent(noShowPolicy, "No Show Policies")}
        </div>)}
        {discounts && (<div className="content-section">
          {this.renderContent(discounts, "Discount Pass Policies")}
        </div>)}
      </Panel>
    );
  }

  renderGettingHere() {
    const {
      latitude,
      longitude
    } = this.props.permit;
    const {
      driving_directions: drivingDirections
    } = this.props.permit.description;

    return (
      <Panel label="Getting Here">
        <h2>Getting Here</h2>
        <div className="permit-venue-row">
          <div className="permit-venue-left-col">
            <h3>GPS Coordinates</h3>
          </div>
          <div className="permit-venue-right-col">
            <p>Latitude {latitude}</p>
            <p>Longitude {longitude}</p>
          </div>
        </div>
        <div className="permit-venue-row">
          <div className="permit-venue-left-col">
            <h3>Driving Directions</h3>
          </div>
          <div className="permit-venue-right-col">
            {this.renderContent(drivingDirections)}
          </div>
        </div>
      </Panel>
    );
  }

  renderContacts() {
    const address1 = get(this.props, 'permit.addresses[0].street_address1');
    const address2 = get(this.props, 'permit.addresses[0].street_address2');
    const address3parts = [
      get(this.props, 'permit.addresses[0].city'),
      get(this.props, 'permit.addresses[0].state_code'),
      get(this.props, 'permit.addresses[0].postal_code')
    ];
    const description1 = get(this.props, 'permit.addresses[0].description1');
    const description2 = get(this.props, 'permit.addresses[0].description2');
    const remarks = get(this.props, 'permit.addresses[0].remarks');
    let address3;

    if (address3parts[0] && address3parts[1] && address3parts[2]) {
      address3 = `${address3parts[0]}, ${address3parts[1]} ${address3parts[2]}`;
    }

    return (
      <Panel label="Contacts">
        <h2>Contact Information</h2>
        {this.props.permit.addresses &&
          <div>
            <div className="permit-venue-row">
              <div className="permit-venue-left-col">
                <h3>Mailing Address</h3>
              </div>
              <div className="permit-venue-right-col">
                {description1 &&
                  <div>{description1}</div>
                }
                {description2 &&
                  <div>{description2}</div>
                }
                {address1 &&
                  <div>{address1}</div>
                }
                {address2 &&
                  <div>{address2}</div>
                }
                {address3 &&
                  <div>{address3}</div>
                }
                {remarks &&
                  <div>{remarks}</div>
                }
              </div>
            </div>
            <div className="permit-venue-row">
              <div className="permit-venue-left-col">
                <h3>Phone Number</h3>
              </div>
              <div className="permit-venue-right-col">
                <p>{this.props.permit.phone}</p>
              </div>
            </div>
          </div>
        }
      </Panel>
    );
  }

  renderSide(hasTitle) {
    switch (this.permitId) {
      case MT_WHITNEY_PERMIT_ID: {
        const currentDate = getCurrentTime(this.props);
        let disableLotteryRegistration = false;

        if (this.props.permit && this.props.userIssuances) {
          disableLotteryRegistration = hasLotteryApplication(this.props.permit, this.props.userIssuances);
        }
        return (
          <PermitAvailability
            key="PermitAvailability"
            hasTitle={hasTitle}
            currentDate={currentDate}
            disableCalendar={this.state.disableCalendar}
            disableLotteryRegistration={disableLotteryRegistration}
            handleImportantDatesClick={this.handleImportantDatesClick}
            division={this.state.division}
            handleDivisionSelection={this.handleDivisionSelection}
            showTheLoginModal={this.showTheLoginModal} />
        );
      }
      default:
        return null;
    }
  }

  renderMap() {
    const { permit: { longitude, latitude, name }, fetchErr } = this.props;
    if (this.state.isLoadingPermitContent || fetchErr) {
      return <Placeholder width="100%" height="600px" />;
    }
    return (
      <PermitMap
        geoJsonCenterPoint={{
          geometry: {
            coordinates: [longitude, latitude],
            type: 'Point',
          },
          properties: {
            name,
            type: 'Permit',
          },
          type: 'Feature',
        }}
        geoJsonLines={this.state.lines}
        geoJsonPoints={this.state.points}
        geoJsonPolygons={this.state.polygons}
        mapContainer="map"
      />
    );
  }

  getLotteries = () =>  {
    const { permit, lotteries } = this.props;
    return lotteries.filter((lottery) => lottery.status === 'LotteryStatusActive')
      .reduce((categorizedLotteries, lottery) => {
        const { open_at, close_at, display_at } = lottery;
        const openTime = moment(open_at).tz(permit.time_zone);
        const closeTime = moment(close_at).tz(permit.time_zone);
        const currentTime = getCurrentTime(this.props)

        if (openTime.isValid() && closeTime.isValid() && currentTime.isBetween(openTime, closeTime)) {
          return {
            ...categorizedLotteries,
            openLotteries: [...categorizedLotteries.openLotteries, {lottery}],
          };
        }

        const displayTime = moment(display_at).tz(permit.time_zone);
        if (displayTime.isValid() && openTime.isValid() && currentTime.isBetween(displayTime, openTime)) {
          return {
            ...categorizedLotteries,
            futureLotteriesList: [...categorizedLotteries.futureLotteriesList, {lottery}],
          };
        }

        return categorizedLotteries;
      }, { openLotteries: [], futureLotteriesList: [] })
  }

  enterLottery = (reservationId, lotteryId) =>  {
      const redirectURL = (`/permits/${this.props.permit.id}/lottery/${lotteryId}/registration/${reservationId}`);
      window.location.assign(redirectURL);
  }

  isClosed = () => {
    const currentDate = getCurrentTime(this.props);
    const displayStart = readRuleAsMoment('DisplayDate', this.props.aggSeasonRules, this.props.permit.time_zone, undefined, true);
    const displayEnd = readRuleAsMoment('SeasonEnd', this.props.aggSeasonRules, this.props.permit.time_zone);

    return !(displayStart.isValid() && displayEnd.isValid() && currentDate.isBetween(displayStart, displayEnd, 'minute', '[]'))
  }

  render() {
    const { permit, recarea, errorList, fetchErr } = this.props;
    const ratingParams = { locationType: 'Permit', locationId: permit.id };
    const recAreaId = recarea?.[0]?.entity_id ?? '';
    const recAreaName = recarea?.[0]?.name ?? '';
    const partOf = recAreaName
      ? {
          title: recAreaName,
          url: recAreaId ? `/camping/gateways/${recAreaId}` : null,
        }
      : null;
    const {
      openLotteries,
      futureLotteriesList,
    } = this.getLotteries();

    const hasLotteries = Boolean(openLotteries.length || futureLotteriesList.length);
    const showCalendar = !this.isClosed();

    return (
      <div className="inyonwhitney-venue-page">
        <VenuePage
          title={this.props.permit.name || ''}
          isLoading={this.state.isLoadingPermitContent}
          inventoryType="Permit"
          assetId={this.permitId}
          favoriteEntityId={this.permitId}
          favoriteEntityType={'permit'}
          rightRail={RightRailContainer({
            permit,
            isClosed: !showCalendar,
            openLotteries,
            handleEnterLottery: this.enterLottery,
            isOldLotteryFormat: true,
            futureLotteriesList,
            isLoading: this.state.isLoadingPermitContent || fetchErr,
            children: showCalendar && [this.renderSide(hasLotteries)],
            handleImportantDates: this.handleImportantDatesClick,
          })}
          tabs={this.tabsToShow()}
          activeTabIndex={this.state.activeTabIndex}
          onActiveTabIndexChange={this.handleOnActiveTabIndexChange}
          breadcrumbPath={this.getBreadcrumbsPath()}
          partOf={partOf}
          activities={permit.activities}
          mapLatitude={permit.latitude}
          mapLongitude={permit.longitude}
          map={this.renderMap()}
          starRating={<RatingSummaryContainer {...ratingParams} />}
          cellRating={<CellSummaryContainer {...ratingParams} />}
          errors={errorList}
        />
      </div>
    );
  }
}

PermitVenuePage.propTypes = propTypes;
PermitVenuePage.defaultProps = defaultProps;

export default connect((state) => {
  const recareaError = state.inyonwhitney.permit.recarea_err;
  const fetchErr = state.inyonwhitney.permit.fetch_err?.error;

  return {
    account: state.auth.account,
    lotteries: state.inyonwhitney.lotteries,
    ld: state.ld,
    navigation: state.navigation,
    permit: state.inyonwhitney.permit.result,
    recarea: state.inyonwhitney.permit.recarea,
    userIssuances: state.inyonwhitney.permit.user_issuances,
    fetchErr,
    errorList: [fetchErr, recareaError].filter(err => err),
    aggSeasonRules: state.inyonwhitney.agg_season_rules,
  };
})(PermitVenuePage);
